/**
 * ShortlistReadyCard — replaces raw JSON in chat when the agent outputs a shortlist.
 *
 * Shows a summary card with candidate name, program/reject counts, and a CTA
 * to open the ShortlistTable artifact panel.
 */

import type { ShortlistData } from "@/api/types";
import { useShortlistStore } from "@/stores/shortlistStore";
import { useChatStore } from "@/stores/chatStore";
import { BUBBLE_WIDTH_FILL } from "./MessageBubble";

interface Props {
  data: ShortlistData;
}

const QUICK_ACTIONS = [
  "Remove a program",
  "Add more safety options",
  "Add Netherlands programs",
  "Re-check deadlines",
  "Show only scholarship-eligible",
] as const;

export function ShortlistReadyCard({ data }: Props) {
  const setLiveData = useShortlistStore((s) => s.setLiveData);
  const openRightPanel = useChatStore((s) => s.openRightPanel);
  const sendMessage = useChatStore((s) => s.sendMessage);
  const isAgentRunning = useChatStore((s) => s.isAgentRunning);

  const programCount = data.evaluations?.length ?? 0;
  const hardRejectCount =
    data.evaluations?.filter(
      (e) => e.risk_level === "hard_reject" || (e.risk_level as string) === "HARD_REJECT",
    ).length ?? 0;
  const compliantCount =
    data.evaluations?.filter(
      (e) => e.risk_level === "compliant" || (e.risk_level as string) === "TARGET" || (e.risk_level as string) === "SAFETY",
    ).length ?? 0;

  const handleOpen = () => {
    setLiveData(data);
    openRightPanel("shortlist");
  };

  return (
    <div className="flex justify-start animate-[fadeInUp_300ms_ease-out]">
      <div className={`${BUBBLE_WIDTH_FILL} rounded-2xl border border-oxy/30 bg-gradient-to-br from-oxy-50 to-surface px-5 py-4 shadow-sm`}>
        {/* Header */}
        <div className="flex items-center gap-2.5 mb-3">
          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-oxy/15 text-oxy">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" style={{ strokeDasharray: 24, strokeDashoffset: 0 }} className="animate-[checkIn_400ms_ease-out]" />
            </svg>
          </span>
          <h3 className="text-sm font-bold text-space">Shortlist Ready</h3>
        </div>

        {/* Candidate name */}
        {data.candidate_name && (
          <p className="text-sm text-space-50 mb-3 font-medium">{data.candidate_name}</p>
        )}

        {/* Stats row */}
        <div className="flex items-center gap-4 text-xs mb-4">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-space-40" />
            <span className="text-space-50">{programCount} program{programCount !== 1 ? "s" : ""}</span>
          </div>
          {compliantCount > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-emerald-700">{compliantCount} compliant</span>
            </div>
          )}
          {hardRejectCount > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-red-500" />
              <span className="text-red-600">{hardRejectCount} rejected</span>
            </div>
          )}
        </div>

        {/* CTA */}
        <button
          onClick={handleOpen}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium
                     rounded-lg bg-primary text-on-primary hover:bg-jove-200 transition-all
                     shadow-sm hover:shadow-md active:scale-[0.98]"
        >
          View Shortlist
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
          </svg>
        </button>

        {/* Quick action chips for refinement */}
        <div className="mt-4 pt-3 border-t border-oxy/20">
          <p className="text-[10px] uppercase tracking-wider text-space-40 font-medium mb-2">Refine shortlist</p>
          <div className="flex flex-wrap gap-1.5">
            {QUICK_ACTIONS.map((label) => (
              <button
                key={label}
                onClick={() => sendMessage(label)}
                disabled={isAgentRunning}
                className="px-3 py-1.5 text-xs font-medium rounded-full
                           border border-jove-60 text-jove bg-surface
                           hover:bg-jove hover:text-white
                           disabled:opacity-40 disabled:cursor-not-allowed
                           transition-all duration-150 active:scale-95"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
